'use client';

import { ArchiMateElement } from '@/lib/archimate/types';

interface PropertiesPanelProps {
  selectedElement: ArchiMateElement | null;
  onUpdateElement: (id: string, updates: Partial<ArchiMateElement>) => void;
}

export default function PropertiesPanel({ selectedElement, onUpdateElement }: PropertiesPanelProps) {
  if (!selectedElement) {
    return (
      <div className="bg-surface p-4 rounded-lg">
        <h3 className="font-bold text-foreground mb-2">Properties</h3>
        <p className="text-sm text-foreground-secondary">Select an element to view its properties.</p>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    onUpdateElement(selectedElement.id, { [name]: value });
  };

  const handlePropertiesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const properties = { ...selectedElement.properties, [name]: value };
    onUpdateElement(selectedElement.id, { properties });
  };

  const properties = (selectedElement.properties || {}) as any;

  return (
    <div className="bg-surface p-4 rounded-lg">
      <h3 className="font-bold text-foreground mb-4">Properties</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground-secondary mb-1">Name</label>
          <input
            type="text"
            name="name"
            value={selectedElement.name || ''}
            onChange={handleInputChange}
            className="w-full p-2 bg-background border border-border rounded-md text-foreground focus:border-accent focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-secondary mb-1">Type</label>
          <select
            name="type"
            value={selectedElement.type}
            onChange={handleInputChange}
            className="w-full p-2 bg-background border border-border rounded-md text-foreground focus:border-accent focus:outline-none"
          >
            <option value="application-component">Application Component</option>
            <option value="application-service">Application Service</option>
            <option value="application-interface">Application Interface</option>
            <option value="data-object">Data Object</option>
            <option value="node">Node</option>
            <option value="device">Device</option>
            <option value="system-software">System Software</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-secondary mb-1">Cost (monthly)</label>
          <input
            type="number"
            name="cost"
            value={properties.cost || ''}
            onChange={handlePropertiesChange}
            className="w-full p-2 bg-background border border-border rounded-md text-foreground focus:border-accent focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-secondary mb-1">Performance (0-1)</label>
          <input
            type="number"
            name="performance"
            step="0.01"
            min="0"
            max="1"
            value={properties.performance || ''}
            onChange={handlePropertiesChange}
            className="w-full p-2 bg-background border border-border rounded-md text-foreground focus:border-accent focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-secondary mb-1">Reliability (0-1)</label>
          <input
            type="number"
            name="reliability"
            step="0.001"
            min="0"
            max="1"
            value={properties.reliability || ''}
            onChange={handlePropertiesChange}
            className="w-full p-2 bg-background border border-border rounded-md text-foreground focus:border-accent focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
}
